// <s-size-guide> (snippets/size-guide.liquid) opens the "Descubra seu tamanho"
// and "Tabela de medidas" modals ([data-guide-open] -> dialog[data-guide]) and
// recommends a size from the chart rows (tr[data-size][data-chest][data-shoulder]),
// which list garment measurements in cm.
//
// Two modes (tabs [data-guide-tab] / panels [data-guide-panel]):
// - "body": estimates the body chest from gender, height, weight and age
//   (BMI-based approximation, see estimateChest) — an approximate suggestion.
// - "tape": the customer types their measured chest (and optionally shoulder).
// The recommended size is the smallest row whose chest is at least body chest +
// data-ease (garment room, cm) and, when typed, whose shoulder is at least the
// measured shoulder. The row is marked with data-recommended in every table.
// It lives inside the product form, so Enter in its inputs calculates instead
// of submitting the form.

// Estimated tolerance (cm) before an estimate is called "above the largest size".
const ESTIMATE_TOLERANCE = 2;

// Approximate body chest circumference (cm) from body data. A BMI-based fit:
// chest grows about 2 cm per BMI point, slightly with height and with age.
function estimateChest({ gender, height, weight, age }) {
  const bmi = weight / (height / 100) ** 2;
  const heightAdjust = (height - (gender === 'female' ? 163 : 175)) * 0.1;
  const ageAdjust = Math.min(3, Math.max(-1, (age - 30) * 0.08));
  const base = gender === 'female' ? 2.0 * bmi + 46 : 2.1 * bmi + 50;
  return base + heightAdjust + ageAdjust;
}

class SSizeGuide extends HTMLElement {
  #ac;
  #mode = 'body';

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('dialog input')) {
        event.preventDefault();
        this.#calculate();
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
  }

  #onClick(event) {
    const opener = event.target.closest('[data-guide-open]');
    if (opener) {
      const dialog = this.querySelector(`dialog[data-guide="${opener.dataset.guideOpen}"]`);
      if (dialog && !dialog.open) dialog.showModal();
      return;
    }
    if (event.target.closest('[data-guide-close]')) {
      event.target.closest('dialog')?.close();
      return;
    }
    // A click on the dialog box itself (not its content) is a click on the backdrop.
    if (event.target instanceof HTMLDialogElement) {
      event.target.close();
      return;
    }
    const tab = event.target.closest('[data-guide-tab]');
    if (tab) {
      this.#setMode(tab.dataset.guideTab);
      return;
    }
    const gender = event.target.closest('[data-guide-gender]');
    if (gender) {
      this.querySelectorAll('[data-guide-gender]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button === gender));
      });
      return;
    }
    if (event.target.closest('[data-guide-calc]')) this.#calculate();
  }

  #setMode(mode) {
    this.#mode = mode;
    this.querySelectorAll('[data-guide-tab]').forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset.guideTab === mode));
    });
    this.querySelectorAll('[data-guide-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.guidePanel !== mode;
    });
    this.#clearResult();
  }

  #number(selector) {
    const value = Number.parseFloat(String(this.querySelector(selector)?.value || '').replace(',', '.'));
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  #clearResult() {
    this.querySelectorAll('tr[data-recommended]').forEach((row) => row.removeAttribute('data-recommended'));
    this.querySelector('[data-guide-result]')?.replaceChildren();
    const error = this.querySelector('[data-guide-error]');
    if (error) error.hidden = true;
  }

  #showError(message, focusSelector) {
    const error = this.querySelector('[data-guide-error]');
    if (error) {
      error.textContent = message || '';
      error.hidden = false;
    }
    this.querySelector(focusSelector)?.focus();
  }

  #calculate() {
    this.#clearResult();
    const ease = Number.parseFloat(this.dataset.ease) || 0;
    let bodyChest;
    let shoulder = null;
    let tolerance = 0;

    if (this.#mode === 'body') {
      const height = this.#number('input[data-guide-height]');
      const weight = this.#number('input[data-guide-weight]');
      const age = this.#number('input[data-guide-age]');
      if (!height || !weight || !age) {
        const missing = !height ? 'input[data-guide-height]' : !weight ? 'input[data-guide-weight]' : 'input[data-guide-age]';
        this.#showError(this.dataset.bodyInvalidMessage, missing);
        return;
      }
      const gender = this.querySelector('[data-guide-gender][aria-pressed="true"]')?.dataset.guideGender || 'male';
      bodyChest = estimateChest({ gender, height, weight, age });
      tolerance = ESTIMATE_TOLERANCE;
    } else {
      bodyChest = this.#number('input[data-guide-chest]');
      shoulder = this.#number('input[data-guide-shoulder]');
      if (!bodyChest) {
        this.#showError(this.dataset.invalidMessage, 'input[data-guide-chest]');
        return;
      }
    }

    const needed = bodyChest + ease;
    const firstTable = this.querySelector('table');
    const rows = firstTable ? [...firstTable.querySelectorAll('tr[data-size]')] : [];
    const fits = (row, slack) => {
      const rowChest = Number.parseFloat(row.dataset.chest);
      const rowShoulder = Number.parseFloat(row.dataset.shoulder);
      if (!(rowChest + slack >= needed)) return false;
      return !shoulder || !Number.isFinite(rowShoulder) || rowShoulder >= shoulder;
    };
    // Exact fit first; an estimate slightly above the largest size still suggests it.
    const match = rows.find((row) => fits(row, 0)) || (tolerance ? rows.find((row) => fits(row, tolerance)) : undefined);

    const result = this.querySelector('[data-guide-result]');
    const template = this.querySelector(match ? 'template[data-guide-found]' : 'template[data-guide-too-big]');
    if (!template || !result) return;
    const content = template.content.cloneNode(true);
    if (match) {
      const size = match.dataset.size;
      const label = content.querySelector('[data-guide-size]');
      if (label) label.textContent = size;
      const note = content.querySelector('[data-guide-estimate-note]');
      if (note) note.hidden = this.#mode !== 'body';
      this.querySelectorAll('tr[data-size]').forEach((row) => {
        row.toggleAttribute('data-recommended', row.dataset.size === size);
      });
    }
    result.append(content);
  }
}

if (!customElements.get('s-size-guide')) customElements.define('s-size-guide', SSizeGuide);
